import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getSimpleStaffDetails } from '../../../redux/staffRelated/staffHandle';
import { 
    Paper, Box, Typography, Avatar, Grid, Chip, Divider, 
    IconButton, Container, CircularProgress, Card, CardContent, Button, Stack
} from '@mui/material';
import { 
    Person as PersonIcon, 
    Email as EmailIcon, 
    Phone as PhoneIcon, 
    LocationOn as LocationOnIcon, 
    ArrowBack as ArrowBackIcon, 
    Work as WorkIcon,
    AttachMoney as AttachMoneyIcon,
    CalendarMonth as CalendarIcon,
    AssignmentInd as AssignmentIndIcon,
    AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';
import { GreenButton } from '../../../components/buttonStyles';

const StaffDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { staff, loading, error } = useSelector((state) => state.staff);

    useEffect(() => {
        dispatch(getSimpleStaffDetails(id));
    }, [id, dispatch]);

    if (loading) {
        return (
            <LoadingWrapper>
                <CircularProgress size={60} />
                <Typography sx={{ mt: 2, color: '#667eea', fontWeight: 600 }}>Loading Staff Profile...</Typography>
            </LoadingWrapper>
        );
    }

    if (error || !staff) {
        return (
            <LoadingWrapper>
                <Typography color="error">{error ? "Error loading staff details" : "Staff not found"}</Typography>
                <GreenButton onClick={() => navigate("/Admin/staff")} sx={{ mt: 2 }}>Back to List</GreenButton>
            </LoadingWrapper>
        );
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NP', {
            style: 'currency',
            currency: 'NPR',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const InfoItem = ({ icon, label, value }) => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {icon} {label}
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a2e', fontWeight: 500 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    return (
        <PageContainer>
            <HeroSection elevation={3}>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton 
                            onClick={() => navigate("/Admin/staff")}
                            sx={{ color: 'white', mr: 2, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>Staff Profile</Typography>
                    </Box>

                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <StyledAvatar 
                                src={staff.photo ? `${process.env.REACT_APP_BASE_URL}/${staff.photo}` : ''}
                            >
                                <PersonIcon sx={{ fontSize: 60 }} />
                            </StyledAvatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                {staff.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                    icon={<WorkIcon sx={{ fontSize: '1rem !important', color: 'white !important' }} />}
                                    label={staff.position} 
                                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} 
                                />
                                <Chip 
                                    icon={<AssignmentIndIcon sx={{ fontSize: '1rem !important', color: 'white !important' }} />}
                                    label={`ID: ${staff._id.slice(-6).toUpperCase()}`} 
                                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }} 
                                />
                            </Box>
                        </Grid>
                        <Grid item>
                            <Button 
                                variant="contained" 
                                startIcon={<AttachMoneyIcon />}
                                onClick={() => navigate("/Admin/salary")}
                                sx={{ 
                                    bgcolor: '#4caf50', 
                                    '&:hover': { bgcolor: '#43a047' },
                                    borderRadius: '12px',
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 700
                                }}
                            >
                                Manage Salary
                            </Button>
                        </Grid>
                    </Grid>
                </Container>
            </HeroSection>

            <MainContent maxWidth="xl">
                <Grid container spacing={4} sx={{ mt: 5 }}>
                    {/* Left Column: Personal Info */}
                    <Grid item xs={12} lg={7}>
                        <SectionTitle variant="h5">Personal Information</SectionTitle>
                        <InfoCard elevation={2}>
                            <CardHeader color="#667eea">
                                <Typography variant="h6">Basic Details</Typography>
                                <PersonIcon />
                            </CardHeader>
                            <StyledCardContent>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <InfoItem icon={<PersonIcon sx={{fontSize: 16}}/>} label="Full Name" value={staff.name} />
                                        <InfoItem icon={<WorkIcon sx={{fontSize: 16}}/>} label="Job Position" value={staff.position} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <InfoItem icon={<PhoneIcon sx={{fontSize: 16}}/>} label="Contact Number" value={staff.phone} />
                                        <InfoItem icon={<LocationOnIcon sx={{fontSize: 16}}/>} label="Current Address" value={staff.address} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                        <Box sx={{ display: 'flex', gap: 4, mt: 2 }}> 
                                        </Box>
                                    </Grid>
                                </Grid>
                            </StyledCardContent>
                        </InfoCard>
                    </Grid>

                    {/* Right Column: Salary & Financials */}
                    <Grid item xs={12} lg={5}>
                        <SectionTitle variant="h5">Financial Summary</SectionTitle>
                        <InfoCard elevation={2}>
                            <CardHeader color="#11998e">
                                <Typography variant="h6">Current Salary Structure</Typography>
                                <AccountBalanceIcon />
                            </CardHeader>
                            <StyledCardContent>
                                {staff.salary && staff.salary.baseSalary > 0 ? (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, p: 2, bgcolor: '#f0fdf4', borderRadius: '16px' }}>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">NET SALARY</Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#166534' }}>
                                                    {formatCurrency(staff.salary.netSalary || staff.salary.baseSalary)}
                                                </Typography>
                                            </Box>
                                            <AttachMoneyIcon sx={{ fontSize: 48, color: '#166534', opacity: 0.2 }} />
                                        </Box>

                                        <Stack spacing={2}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="textSecondary">Base Salary</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(staff.salary.baseSalary)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="textSecondary">Total Allowances</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#10b981' }}>
                                                    +{formatCurrency(
                                                        (staff.salary.allowances?.houseRent || 0) +
                                                        (staff.salary.allowances?.medical || 0) +
                                                        (staff.salary.allowances?.transport || 0) +
                                                        (staff.salary.allowances?.other || 0)
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="textSecondary">Total Deductions</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#ef4444' }}>
                                                    -{formatCurrency(
                                                        (staff.salary.deductions?.providentFund || 0) +
                                                        (staff.salary.deductions?.tax || 0) +
                                                        (staff.salary.deductions?.insurance || 0) +
                                                        (staff.salary.deductions?.other || 0)
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        
                                        <Divider sx={{ my: 3 }} />
                                        
                                        <GreenButton fullWidth variant="outlined" startIcon={<CalendarIcon />} onClick={() => navigate("/Admin/salary")}>
                                            View Payment History
                                        </GreenButton>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <AccountBalanceIcon sx={{ fontSize: 64, color: '#e5e7eb', mb: 2 }} />
                                        <Typography color="textSecondary" gutterBottom>No salary record configured.</Typography>
                                        <Button variant="contained" color="primary" onClick={() => navigate("/Admin/salary")} sx={{ mt: 2, borderRadius: '12px' }}>
                                            Setup Payroll
                                        </Button>
                                    </Box>
                                )}
                            </StyledCardContent>
                        </InfoCard>
                    </Grid>
                </Grid>
            </MainContent>
        </PageContainer>
    );
};

export default StaffDetails;

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const PageContainer = styled.div`background-color: #f8fafc; min-height: 100vh;`;
const HeroSection = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;
    border-radius: 0 0 40px 40px !important; padding: 60px 0 !important; color: white !important;
`;
const StyledAvatar = styled(Avatar)`
    width: 140px !important; height: 140px !important; border: 6px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); box-shadow: 0 8px 25px rgba(0,0,0,0.2);
`;
const MainContent = styled(Container)`margin-top: -40px; padding-bottom: 60px; animation: ${fadeIn} 0.5s ease-out;`;
const SectionTitle = styled(Typography)`
    font-weight: 800 !important; color: #1a1a2e; margin-bottom: 24px !important;
    padding-left: 8px; border-left: 5px solid #667eea;
`;
const InfoCard = styled(Paper)`border-radius: 24px !important; overflow: hidden; height: 100%; border: 1px solid #edf2f7;`;
const CardHeader = styled(Box)`
    padding: 16px 24px; background: ${props => props.color || '#667eea'}; color: white;
    display: flex; justify-content: space-between; align-items: center;
`;
const StyledCardContent = styled(Box)`padding: 24px;`;
const LoadingWrapper = styled(Box)`display: flex; flex-direction: column; justify-content: center; align-items: center; height: 80vh;`;
